import { listed, plural } from './format.js';
import { MAX_LISTED, age, eventsAbout, groupBy, hasItems, hoursBetween, insideOf, kindOf, workloadOf, } from './inside.js';
import { isEvaluation } from './model.js';
import { REPLICA_RULES } from './replica-rules.js';
import { KEDA_DEFAULT_POLLING_SECONDS, THRESHOLDS } from './thresholds.js';
/**
 * How workloads behave once placed (IM-206): restarting, scaling up and down
 * faster than they start, running at a replica count the product forbids, or
 * not being created at all.
 */
const W = THRESHOLDS.workloads;
/** The cluster keeps events for about an hour, so every count from them says so. */
const LAST_HOUR = 'in the cluster’s last hour of events';
/** How a container last stopped, in plain words. */
const stoppedBy = (c) => {
    const last = c.lastTermination;
    if (last === undefined)
        return undefined;
    if (last.reason === 'OOMKilled')
        return 'out of memory';
    if (last.exitCode === 0)
        return 'exited cleanly';
    if (last.exitCode === 137 || last.exitCode === 143)
        return 'killed';
    return last.exitCode === undefined
        ? last.reason
        : `exit ${String(last.exitCode)}${last.reason === undefined ? '' : ` (${last.reason})`}`;
};
/**
 * Restarts per hour per workload, and why they stopped (IM-206).
 *
 * A pod's restart count runs from when the pod started, so the rate is that
 * count over the pod's age — never less than an hour, or one restart in a
 * pod's first minute would read as sixty an hour. A container killed after
 * failing its liveness probe and one killed for memory look the same in the
 * count and are different fixes, so the last stop of each is named, and
 * liveness failures are counted from the cluster's events beside it.
 *
 * Job pods are left out: a job's retries are the job's business, and its pod
 * is gone before a rate means anything.
 */
export const restartsCheck = {
    id: 'restarts',
    title: 'Workloads are not restarting',
    category: 'cluster',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const pods = kindOf(inside.value, 'pods', 'the pods');
        if (!hasItems(pods))
            return pods.outcome === 'not-applicable' ? { ...pods, outcome: 'unknown' } : pods;
        const eventsRead = kindOf(inside.value, 'events', 'the events');
        const events = hasItems(eventsRead) ? eventsRead.items : [];
        const rate = (pod) => {
            const since = pod.startedAt ?? pod.createdAt;
            const hours = since === undefined ? 1 : Math.max(1, hoursBetween(since, facts.at));
            return pod.containers.reduce((n, c) => n + (c.restartCount ?? 0), 0) / hours;
        };
        const running = pods.items.filter((p) => p.owner?.kind !== 'Job');
        const workloads = [...groupBy(running, workloadOf).entries()]
            .map(([workload, members]) => {
            const liveness = eventsAbout(events, members)
                .filter((e) => e.reason === 'Unhealthy' && /liveness/i.test(e.message ?? ''))
                .reduce((n, e) => n + (e.count ?? 1), 0);
            const stops = members.flatMap((m) => m.containers.flatMap((c) => {
                const why = stoppedBy(c);
                return why === undefined ? [] : [why];
            }));
            return {
                workload,
                perHour: members.reduce((n, m) => n + rate(m), 0),
                restarts: members.reduce((n, m) => n + m.containers.reduce((k, c) => k + (c.restartCount ?? 0), 0), 0),
                liveness,
                stops,
            };
        })
            .filter((w) => w.perHour >= W.restartsWarnPerHour)
            .sort((a, b) => b.perHour - a.perHour);
        if (workloads.length === 0) {
            return {
                outcome: 'ok',
                finding: `No workload restarts as often as once an hour.`,
                evidence: [{ label: 'Compared', value: plural(running.length, 'pod') }],
            };
        }
        const describe = (w) => {
            const counts = [...groupBy(w.stops, (s) => s).entries()].map(([why, all]) => `${why} ${String(all.length)}×`);
            return [
                `about ${String(Math.round(w.perHour * 10) / 10)} an hour (${plural(w.restarts, 'restart')} in all)`,
                counts.length === 0 ? undefined : `last stopped: ${counts.join(', ')}`,
                w.liveness === 0
                    ? undefined
                    : `liveness probe failed ${plural(w.liveness, 'time')} ${LAST_HOUR}`,
            ]
                .filter((part) => part !== undefined)
                .join(' — ');
        };
        const evidence = workloads
            .slice(0, MAX_LISTED)
            .map((w) => ({ label: w.workload, value: describe(w) }));
        const worst = workloads[0];
        const outOfMemory = workloads.some((w) => w.stops.includes('out of memory'));
        const probeKilled = workloads.some((w) => w.liveness > 0);
        return {
            outcome: (worst?.perHour ?? 0) >= W.restartsFailPerHour ? 'fail' : 'warn',
            finding: `${listed(workloads.slice(0, 3).map((w) => w.workload))}${workloads.length > 3 ? ` and ${plural(workloads.length - 3, 'more')}` : ''} ${workloads.length === 1 ? 'restarts' : 'restart'} at least once an hour.`,
            recommendation: [
                probeKilled
                    ? 'A liveness probe that fails on a busy node kills a working pod and loses what it was doing; give it a CPU request it can rely on, or a longer failure threshold.'
                    : undefined,
                outOfMemory ? 'Raise the memory limit of a workload stopped for memory.' : undefined,
                !probeKilled && !outOfMemory
                    ? 'Read the last log of a restarted pod for why it stopped.'
                    : undefined,
            ]
                .filter((part) => part !== undefined)
                .join(' '),
            evidence,
        };
    },
};
/** A trigger that scales on a queue's length, with the target it holds each pod to. */
const queueTarget = (t) => {
    if (t.type === 'cpu' || t.type === 'memory')
        return undefined;
    const raw = t.settings['queueLength'] ?? t.settings['value'];
    if (raw === undefined)
        return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
};
/**
 * KEDA ScaledObjects that scale faster than their pods can start (IM-206).
 *
 * A queue target below one message per pod adds pods for messages already
 * handed out, then removes them a poll later; a polling interval of a few
 * seconds reacts to each burst before the last one's pods have started. On
 * one instance the two together started about 360 pods an hour with cold
 * starts of 40 to 250 seconds. Pods created in the last hour that are still
 * here are the evidence — the ones that came and went are not listed by the
 * cluster, so the count is a floor.
 */
export const autoscalerThrashCheck = {
    id: 'autoscaler-thrash',
    title: 'Autoscalers do not scale faster than pods start',
    category: 'cluster',
    outcomes: ['ok', 'warn', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const scaled = kindOf(inside.value, 'scaledObjects', 'the KEDA ScaledObjects');
        if (!hasItems(scaled))
            return scaled;
        if (scaled.items.length === 0) {
            return { outcome: 'not-applicable', finding: 'No workload scales with KEDA.', evidence: [] };
        }
        const podsRead = kindOf(inside.value, 'pods', 'the pods');
        const pods = hasItems(podsRead) ? podsRead.items : [];
        const hpasRead = kindOf(inside.value, 'horizontalPodAutoscalers', 'the autoscalers');
        const hpas = hasItems(hpasRead) ? hpasRead.items : [];
        const flagged = scaled.items.flatMap((so) => {
            const polling = so.pollingInterval ?? KEDA_DEFAULT_POLLING_SECONDS;
            const targets = so.triggers.flatMap((t) => {
                const target = queueTarget(t);
                return target === undefined ? [] : [target];
            });
            const lowTarget = targets.find((t) => t < W.minQueueTargetPerPod);
            const fastPolling = polling < W.minPollingSeconds;
            if (lowTarget === undefined && !fastPolling)
                return [];
            const started = pods.filter((p) => p.namespace === so.namespace &&
                workloadOf(p) === so.target.name &&
                p.createdAt !== undefined &&
                hoursBetween(p.createdAt, facts.at) <= 1).length;
            const hpa = hpas.find((h) => h.namespace === so.namespace && h.target.name === so.target.name);
            return [
                {
                    name: so.name,
                    value: [
                        lowTarget === undefined ? undefined : `queue target ${String(lowTarget)} per pod`,
                        `polling every ${String(polling)} s`,
                        `${String(so.minReplicas ?? 0)} to ${String(so.maxReplicas ?? '?')} replicas`,
                        `${plural(started, 'pod')} started in the last hour still running`,
                        hpa?.currentReplicas === undefined
                            ? undefined
                            : `${String(hpa.currentReplicas)} running, ${String(hpa.desiredReplicas ?? '?')} wanted`,
                    ]
                        .filter((part) => part !== undefined)
                        .join(' — '),
                },
            ];
        });
        if (flagged.length === 0) {
            return {
                outcome: 'ok',
                finding: `All ${plural(scaled.items.length, 'ScaledObject')} poll at ${String(W.minPollingSeconds)} s or slower, with queue targets of at least one message per pod.`,
                evidence: [],
            };
        }
        return {
            outcome: 'warn',
            finding: `${plural(flagged.length, 'workload')} ${flagged.length === 1 ? 'scales' : 'scale'} on settings that start pods faster than they can be useful.`,
            recommendation: `Set queue targets of at least one message per pod, polling of ${String(W.minPollingSeconds)} seconds or more, and a scale-down stabilisation window, so pods live long enough to work.`,
            evidence: flagged.slice(0, MAX_LISTED).map((f) => ({ label: f.name, value: f.value })),
        };
    },
};
/**
 * Workloads the product requires at an exact replica count (IM-206).
 *
 * `REPLICA_RULES` holds each rule with its reason and where the fact comes
 * from, and the finding says both: a count changed by hand to "fix" a slow
 * service is exactly how the first rule was learned.
 */
export const replicaRulesCheck = {
    id: 'replica-rules',
    title: 'Workloads that must run alone do',
    category: 'cluster',
    outcomes: ['ok', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const deployments = kindOf(inside.value, 'deployments', 'the Deployments');
        if (!hasItems(deployments))
            return deployments;
        const sets = kindOf(inside.value, 'statefulSets', 'the StatefulSets');
        const controllers = [...deployments.items, ...(hasItems(sets) ? sets.items : [])];
        const matched = REPLICA_RULES.flatMap((rule) => {
            const found = controllers.find((c) => c.name === rule.workload || rule.aliases.includes(c.name));
            return found === undefined ? [] : [{ rule, found }];
        });
        if (matched.length === 0) {
            return {
                outcome: 'not-applicable',
                finding: 'No workload here has a fixed replica count.',
                evidence: [],
            };
        }
        const broken = matched.filter(({ rule, found }) => (found.desired ?? 0) > rule.replicas || (found.ready ?? 0) > rule.replicas);
        const evidence = matched.map(({ rule, found }) => ({
            label: found.name,
            value: `${String(found.desired ?? '?')} wanted, ${String(found.ready ?? 0)} ready — must be ${String(rule.replicas)}. ${rule.source}`,
        }));
        if (broken.length > 0) {
            return {
                outcome: 'fail',
                finding: `${listed(broken.map((b) => b.found.name))} ${broken.length === 1 ? 'runs' : 'run'} at more replicas than the product allows. ${broken.map((b) => b.rule.why).join(' ')}`,
                recommendation: `Scale ${listed(broken.map((b) => b.found.name))} back to ${listed(broken.map((b) => String(b.rule.replicas)))}. If it is slow, give it more CPU rather than more replicas.`,
                evidence,
            };
        }
        return {
            outcome: 'ok',
            finding: `${listed(matched.map((m) => m.found.name))} ${matched.length === 1 ? 'runs' : 'run'} at the replica count the product requires.`,
            evidence,
        };
    },
};
/** The controller an event's object belongs to, by name, the way pods are grouped. */
const controllerOf = (e) => e.object.kind === 'Job' ? e.object.name.replace(/-[0-9]{6,}$/, '') : e.object.name;
/**
 * Whether a refused workload has since got its pods, or `undefined` when
 * nothing read can say. Judged from the controllers the cluster reader already
 * holds, so it costs no read of its own:
 *
 * - a scheduled job's newest run is active or has succeeded;
 * - a lone job is active or has succeeded;
 * - a ReplicaSet's Deployment, a StatefulSet or a DaemonSet has every replica
 *   it wants ready.
 *
 * Only the newest run of a schedule counts, because the cluster keeps a few
 * and deletes the rest: a refused run that was later retried is usually gone,
 * and what is left says whether the schedule runs now.
 */
/**
 * Orders a schedule's runs oldest first.
 *
 * By the scheduled time a CronJob writes into each run's name (minutes since
 * the epoch), which every run has from the moment it exists. `startedAt` is
 * the fallback for a lone job, and a run with none sorts **newest**: a job
 * Kueue holds suspended, or one the controller has not started, has no start
 * time and is exactly the run most likely to be the refused one. Sorting it
 * oldest would let a finished older run vouch for it.
 */
const newerLast = (a, b) => {
    const scheduled = (j) => {
        const m = /-([0-9]{6,})$/.exec(j.name);
        return m === null ? undefined : Number(m[1]);
    };
    const sa = scheduled(a);
    const sb = scheduled(b);
    if (sa !== undefined && sb !== undefined && sa !== sb)
        return sa - sb;
    if (a.startedAt === undefined)
        return b.startedAt === undefined ? 0 : 1;
    if (b.startedAt === undefined)
        return -1;
    return a.startedAt.localeCompare(b.startedAt);
};
const hasRecovered = (kind, workload, namespace, read) => {
    const here = (items) => items.filter((i) => namespace === undefined || i.namespace === namespace);
    const ran = (job) => (job.active ?? 0) > 0 || (job.succeeded ?? 0) > 0;
    /*
     * Every replica ready *and* updated. Ready alone counts the old ReplicaSet's
     * pods during a rollout, so a refused new ReplicaSet would hide behind the
     * Deployment's total; Kubernetes omits a zero `updatedReplicas`, which reads
     * here as none updated.
     */
    const whole = (c) => c.desired !== undefined && (c.ready ?? 0) >= c.desired && (c.updated ?? 0) >= c.desired;
    switch (kind) {
        case 'Job': {
            if (read.jobs === undefined)
                return undefined;
            const runs = here(read.jobs).filter((j) => j.name === workload ||
                (j.owner?.kind === 'CronJob' && j.owner.name === workload) ||
                j.name.replace(/-[0-9]{6,}$/, '') === workload);
            const newest = [...runs].sort(newerLast).at(-1);
            return newest === undefined ? undefined : ran(newest);
        }
        case 'ReplicaSet': {
            // By equality once the template hash is off, as `workloadOf` groups
            // pods: a prefix would let a healthy `qrvey` vouch for `qrvey-api`.
            const name = workload.replace(/-[a-z0-9]{6,10}$/, '');
            const owner = here(read.controllers['Deployment'] ?? []).find((d) => d.name === name);
            return owner === undefined ? undefined : whole(owner);
        }
        default: {
            const found = here(read.controllers[kind] ?? []).find((c) => c.name === workload);
            return found === undefined ? undefined : whole(found);
        }
    }
};
/**
 * Workloads the cluster refuses to create pods for (IM-206).
 *
 * Not in the story: the first live read of an open cluster found it. Every
 * scheduled job of one instance was refused by an admission webhook, and
 * nothing on any screen said so. A `FailedCreate` warning on a Job,
 * ReplicaSet, StatefulSet or DaemonSet means the cluster was refused a pod it
 * asked for, whoever refused it.
 *
 * **A refusal the cluster retried past is a warning, not a failure.** The
 * first investigation (IM-209, 2026-11-03) read that same instance and found
 * the refusals real and short-lived: Kueue's pod webhook looked up each new
 * job before it could see it, answered "not found", and the next attempt was
 * created — every schedule's newest run had finished. Calling that "not
 * running" sent the reader after an outage that was not there. So a workload
 * that has since got its pods (`hasRecovered`) is listed as recovered, and
 * only one that has not, or that nothing read can vouch for, fails.
 */
export const workloadCreationCheck = {
    id: 'workload-creation',
    title: 'The cluster creates the pods it is asked for',
    category: 'cluster',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const events = kindOf(inside.value, 'events', 'the events');
        if (!hasItems(events)) {
            return events.outcome === 'not-applicable' ? { ...events, outcome: 'unknown' } : events;
        }
        const refused = events.items.filter((e) => e.reason === 'FailedCreate' &&
            ['Job', 'ReplicaSet', 'StatefulSet', 'DaemonSet'].includes(e.object.kind));
        if (refused.length === 0) {
            return {
                outcome: 'ok',
                finding: `No workload was refused a pod ${LAST_HOUR}.`,
                evidence: [],
            };
        }
        // A kind that could not be read cannot vouch for anything: its workloads stay failing.
        const items = (id, what) => {
            const got = kindOf(inside.value, id, what);
            return hasItems(got) ? got.items : undefined;
        };
        const read = {
            jobs: items('jobs', 'the Jobs'),
            controllers: {
                Deployment: items('deployments', 'the Deployments'),
                StatefulSet: items('statefulSets', 'the StatefulSets'),
                DaemonSet: items('daemonSets', 'the DaemonSets'),
            },
        };
        const groups = [...groupBy(refused, controllerOf).entries()]
            .map(([workload, all]) => ({
            workload,
            times: all.reduce((n, e) => n + (e.count ?? 1), 0),
            message: all.find((e) => e.message !== undefined)?.message,
            latest: all
                .map((e) => e.lastSeen ?? '')
                .sort()
                .at(-1),
            recovered: hasRecovered(all[0]?.object.kind ?? '', workload, all[0]?.object.namespace, read) ===
                true,
        }))
            // What is still refused first, then by how often.
            .sort((a, b) => Number(a.recovered) - Number(b.recovered) || b.times - a.times);
        const evidence = groups.slice(0, MAX_LISTED).map((g) => ({
            label: g.workload,
            value: [
                `refused ${plural(g.times, 'time')}`,
                g.latest === undefined || g.latest === ''
                    ? undefined
                    : `last ${age(hoursBetween(g.latest, facts.at))} ago`,
                g.recovered ? 'has since got its pods' : 'no pods since',
                g.message,
            ]
                .filter((part) => part !== undefined)
                .join(' — '),
        }));
        if (groups.length > MAX_LISTED) {
            evidence.push({ label: 'And', value: plural(groups.length - MAX_LISTED, 'more workload') });
        }
        const stuck = groups.filter((g) => !g.recovered);
        if (stuck.length === 0) {
            return {
                outcome: 'warn',
                finding: `The cluster refused pods for ${plural(groups.length, 'workload')} ${LAST_HOUR}, and ${groups.length === 1 ? 'it has' : 'each has'} since got them, so nothing has stopped running.`,
                recommendation: 'A refusal the cluster retries past is usually an admission webhook answering before it can see the object it was asked about. The message names the webhook; limiting it to the workloads it manages stops the refusals.',
                evidence,
            };
        }
        return {
            outcome: 'fail',
            finding: `The cluster refused to create pods for ${plural(stuck.length, 'workload')} ${LAST_HOUR}, and ${stuck.length === 1 ? 'it has' : 'they have'} had none since: ${listed(stuck.map((g) => g.workload))}.`,
            recommendation: 'The message names what refused: an admission webhook, a quota, or a missing service account. A webhook that cannot find the job it is asked about is usually one whose own controller is unhealthy.',
            evidence,
        };
    },
};
//# sourceMappingURL=behaviour.js.map